import { useCallback, useEffect } from 'react'
import {
  Avatar,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  styled,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Flag as FlagIcon,
  Sort as SortIcon,
} from '@mui/icons-material'
import { Icon } from '@iconify/react'
import moment from 'moment'
import { MilestoneDetail } from '../../../../../domain/entity/MilestoneEntity'
import { DISPLAY_DATE_FORMAT } from '../../../../../utils/date'
import BigFireIcon from '../../../../components/icons/BigFireIcon'
import { MarkdownPreview } from '../../../../components/editors/markdown/Markdown/MarkdownPreview'
import '../../../../components/editors/markdown/Markdown/styles.scss'
import { Button } from '../../../../components/buttons'
import { colorPalette } from '../../../../style/colorPalette'
import { intl } from '../../../../../i18n'
import {
  useMilestoneDialog,
  MilestoneWithState,
  SortOption,
  FormState,
  DEFAULT_ICON,
  DEFAULT_COLOR,
  getMilestoneStatus,
} from '../../hooks'
import { MilestoneFormFields } from './MilestoneFormFields'

type Props = {
  open: boolean
  onClose: () => void
  initialMilestones?: MilestoneDetail[]
  initialSelectedUuid?: string
  onSaveComplete?: () => void
  isLoadingMilestones?: boolean
}

const MilestoneDialog = ({
  open,
  onClose,
  initialMilestones,
  initialSelectedUuid,
  onSaveComplete,
  isLoadingMilestones,
}: Props) => {
  const {
    displayMilestones,
    selectedMilestone,
    isSaving,
    rightPanelMode,
    sortOption,
    setSortOption,
    formState,
    hasFormChanges,
    deleteConfirmDialogOpen,
    deleteTargetMilestone,
    discardConfirmDialogOpen,
    handleDialogEntered,
    handleCloseDialog,
    handleSelectMilestone,
    handleStartEdit,
    handleUpdateField,
    handleToggleAchieved,
    handleCancelEdit,
    handleSaveEdit,
    handleRequestDelete,
    handleCancelDelete,
    handleConfirmDelete,
    handleCancelDiscard,
    handleConfirmDiscard,
  } = useMilestoneDialog({
    initialMilestones,
    initialSelectedUuid,
    onClose,
    onSaveComplete,
  })

  const isLoading = isLoadingMilestones ?? false

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        e.stopPropagation()
        if (rightPanelMode === 'edit' && !isSaving && hasFormChanges) {
          handleSaveEdit()
        }
      }
    },
    [rightPanelMode, isSaving, hasFormChanges, handleSaveEdit]
  )

  useEffect(() => {
    if (!open) return

    window.addEventListener('keydown', handleKeyDown, true)
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [open, handleKeyDown])

  return (
    <StyledDialog
      open={open}
      onClose={handleCloseDialog}
      maxWidth={false}
      TransitionProps={{ onEnter: handleDialogEntered }}
    >
      <StyledDialogTitle>
        <TitleContent>
          <StyledFlagIcon />
          <Typography variant="h6" component="span">
            {intl.formatMessage({ id: 'milestone.dialog.title' })}
          </Typography>
          {hasFormChanges && (
            <UnsavedChip
              label={intl.formatMessage({ id: 'milestone.unsavedChanges' })}
              size="small"
            />
          )}
        </TitleContent>
        <IconButton onClick={handleCloseDialog} size="small">
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        <ContentWrapper>
          <LeftPanel
            milestones={displayMilestones}
            selectedMilestone={selectedMilestone}
            sortOption={sortOption}
            isLoading={isLoading}
            onSortChange={setSortOption}
            onSelect={handleSelectMilestone}
            onToggleAchieved={handleToggleAchieved}
          />

          <RightPanelWrapper>
            {rightPanelMode === 'view' && selectedMilestone && (
              <ViewPanel
                milestone={selectedMilestone}
                isSaving={isSaving}
                onEdit={handleStartEdit}
                onDelete={handleRequestDelete}
              />
            )}

            {rightPanelMode === 'edit' && selectedMilestone && (
              <>
                <FormPanel
                  formState={formState}
                  onFieldChange={handleUpdateField}
                />
                <RightPanelFooter>
                  <Button
                    colorPattern="monotone"
                    variant="outlined"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                  >
                    {intl.formatMessage({ id: 'milestone.button.cancel' })}
                  </Button>
                  <SaveButton
                    colorPattern="skyBlue"
                    onClick={handleSaveEdit}
                    disabled={isSaving || !hasFormChanges}
                  >
                    {isSaving && <CircularProgress size={16} color="inherit" />}
                    {isSaving
                      ? intl.formatMessage({ id: 'milestone.button.saving' })
                      : intl.formatMessage({ id: 'milestone.button.save' })}
                  </SaveButton>
                </RightPanelFooter>
              </>
            )}

            {rightPanelMode === 'view' && !selectedMilestone && (
              <EmptyStateContainer>
                <Typography fontWeight={600}>
                  {intl.formatMessage({ id: 'milestone.emptyState.title' })}
                </Typography>
                <Typography variant="body2">
                  {intl.formatMessage({ id: 'milestone.emptyState.message' })}
                </Typography>
              </EmptyStateContainer>
            )}
          </RightPanelWrapper>
        </ContentWrapper>
      </StyledDialogContent>

      <Dialog
        open={deleteConfirmDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="xs"
      >
        <DialogTitle>
          {intl.formatMessage({ id: 'milestone.confirm.deleteTitle' })}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {intl.formatMessage(
              { id: 'milestone.confirm.deleteMessage' },
              { name: deleteTargetMilestone?.name ?? '' }
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            colorPattern="monotone"
            variant="outlined"
            onClick={handleCancelDelete}
          >
            {intl.formatMessage({ id: 'milestone.button.cancel' })}
          </Button>
          <Button
            colorPattern="pink"
            onClick={handleConfirmDelete}
            disabled={isSaving}
          >
            {isSaving && <CircularProgress size={16} color="inherit" />}
            {intl.formatMessage({ id: 'milestone.action.delete' })}
          </Button>
        </DialogActions>
      </Dialog>

      <DiscardConfirmDialog
        open={discardConfirmDialogOpen}
        onClose={handleCancelDiscard}
      >
        <DialogTitle>
          {intl.formatMessage({ id: 'milestone.confirm.discardTitle' })}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {intl.formatMessage({ id: 'milestone.confirm.discardMessage' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            colorPattern="monotone"
            variant="outlined"
            onClick={handleCancelDiscard}
          >
            {intl.formatMessage({ id: 'milestone.confirm.cancel' })}
          </Button>
          <Button colorPattern="pink" onClick={handleConfirmDiscard}>
            {intl.formatMessage({ id: 'milestone.confirm.discardButton' })}
          </Button>
        </DialogActions>
      </DiscardConfirmDialog>
    </StyledDialog>
  )
}

const LeftPanel = ({
  milestones,
  selectedMilestone,
  sortOption,
  isLoading,
  onSortChange,
  onSelect,
  onToggleAchieved,
}: {
  milestones: MilestoneWithState[]
  selectedMilestone: MilestoneWithState | null
  sortOption: SortOption
  isLoading: boolean
  onSortChange: (option: SortOption) => void
  onSelect: (uuid: string) => void
  onToggleAchieved: (uuid: string) => void
}) => (
  <LeftPanelContainer>
    <LeftPanelHeader>
      <Typography variant="subtitle2" fontWeight={600}>
        {intl.formatMessage(
          { id: 'milestone.list.title' },
          { count: milestones.length }
        )}
      </Typography>
      <SortContainer>
        <StyledSortIcon />
        <StyledFormControl size="small">
          <StyledSelect
            value={sortOption}
            onChange={e => onSortChange(e.target.value as SortOption)}
          >
            <MenuItem value="date_asc">
              {intl.formatMessage({ id: 'milestone.sort.dateAsc' })}
            </MenuItem>
            <MenuItem value="date_desc">
              {intl.formatMessage({ id: 'milestone.sort.dateDesc' })}
            </MenuItem>
            <MenuItem value="achieved_first">
              {intl.formatMessage({ id: 'milestone.sort.achievedFirst' })}
            </MenuItem>
            <MenuItem value="unachieved_first">
              {intl.formatMessage({ id: 'milestone.sort.unachievedFirst' })}
            </MenuItem>
            <MenuItem value="created_asc">
              {intl.formatMessage({ id: 'milestone.sort.createdAsc' })}
            </MenuItem>
            <MenuItem value="created_desc">
              {intl.formatMessage({ id: 'milestone.sort.createdDesc' })}
            </MenuItem>
          </StyledSelect>
        </StyledFormControl>
      </SortContainer>
    </LeftPanelHeader>

    <StyledList dense={true}>
      {isLoading ? (
        <LoadingContainer>
          <CircularProgress size={32} />
        </LoadingContainer>
      ) : milestones.length === 0 ? (
        <EmptyListContainer>
          <Typography color="text.secondary">
            {intl.formatMessage({ id: 'milestone.empty' })}
          </Typography>
        </EmptyListContainer>
      ) : null}
      {milestones.map(milestone => (
        <MilestoneListItem
          key={milestone.uuid}
          milestone={milestone}
          isSelected={selectedMilestone?.uuid === milestone.uuid}
          onSelect={onSelect}
          onToggleAchieved={onToggleAchieved}
        />
      ))}
    </StyledList>
  </LeftPanelContainer>
)

const MilestoneListItem = ({
  milestone,
  isSelected,
  onSelect,
  onToggleAchieved,
}: {
  milestone: MilestoneWithState
  isSelected: boolean
  onSelect: (uuid: string) => void
  onToggleAchieved: (uuid: string) => void
}) => {
  const status = getMilestoneStatus(milestone)

  return (
    <StyledListItem
      isSelected={isSelected}
      onClick={() => onSelect(milestone.uuid)}
    >
      <Tooltip
        title={
          status === 'achieved'
            ? intl.formatMessage({ id: 'milestone.status.achieved' })
            : status === 'overdue'
              ? intl.formatMessage({ id: 'milestone.status.overdue' })
              : intl.formatMessage({ id: 'milestone.status.pending' })
        }
      >
        <AchievedIconButton
          size="small"
          onClick={e => {
            e.stopPropagation()
            onToggleAchieved(milestone.uuid)
          }}
        >
          {status === 'achieved' ? (
            <StyledCheckCircleIcon />
          ) : status === 'overdue' ? (
            <BigFireIcon style={{ fontSize: 20 }} />
          ) : (
            <StyledCheckCircleOutlineIcon />
          )}
        </AchievedIconButton>
      </Tooltip>
      <StyledListItemIcon iconColor={milestone.color ?? DEFAULT_COLOR}>
        <Icon
          icon={milestone.iconName ?? DEFAULT_ICON}
          width={18}
          height={18}
          color={milestone.color ?? DEFAULT_COLOR}
        />
      </StyledListItemIcon>
      <ListItemText
        primary={milestone.name}
        secondary={moment(milestone.milestoneDate).format(DISPLAY_DATE_FORMAT)}
        primaryTypographyProps={{ fontSize: 13 }}
        secondaryTypographyProps={{ fontSize: 11 }}
      />
    </StyledListItem>
  )
}

const ViewPanel = ({
  milestone,
  isSaving,
  onEdit,
  onDelete,
}: {
  milestone: MilestoneWithState
  isSaving: boolean
  onEdit: (uuid: string) => void
  onDelete: (uuid: string) => void
}) => {
  const status = getMilestoneStatus(milestone)

  return (
    <>
      <PanelHeader>
        <PanelHeaderContent>
          <Typography variant="subtitle2" fontWeight={600}>
            {intl.formatMessage({ id: 'milestone.panel.basicInfo' })}
          </Typography>
          <ActionButtonsContainer>
            <Button
              colorPattern="skyBlue"
              variant="outlined"
              onClick={() => onEdit(milestone.uuid)}
            >
              <SmallEditIcon />
              {intl.formatMessage({ id: 'milestone.button.edit' })}
            </Button>
            <Button
              colorPattern="monotone"
              variant="outlined"
              onClick={() => onDelete(milestone.uuid)}
              disabled={isSaving}
            >
              <SmallDeleteIcon />
              {intl.formatMessage({ id: 'milestone.action.delete' })}
            </Button>
          </ActionButtonsContainer>
        </PanelHeaderContent>
      </PanelHeader>

      <PanelContent>
        <FieldsContainer>
          <StatusBadge status={status}>
            {status === 'achieved' ? (
              <>
                <StyledCheckCircleIconView />
                <StatusText variant="body2" status={status}>
                  {intl.formatMessage({ id: 'milestone.status.achieved' })}
                  {milestone.achievedAt && ` (${milestone.achievedAt})`}
                </StatusText>
              </>
            ) : status === 'overdue' ? (
              <>
                <BigFireIcon style={{ fontSize: 21 }} />
                <StatusText variant="body2" status={status}>
                  {intl.formatMessage({
                    id: 'milestone.status.overdueMessage',
                  })}
                </StatusText>
              </>
            ) : (
              <>
                <StyledCheckCircleOutlineIconView />
                <StatusText variant="body2" status={status}>
                  {intl.formatMessage({ id: 'milestone.status.pending' })}
                </StatusText>
              </>
            )}
          </StatusBadge>

          <div>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {intl.formatMessage({ id: 'milestone.field.name' })}
            </Typography>
            <Typography variant="body2">{milestone.name}</Typography>
          </div>

          <div>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {intl.formatMessage({ id: 'milestone.field.date' })}
            </Typography>
            <Typography variant="body2">
              {moment(milestone.milestoneDate).format(DISPLAY_DATE_FORMAT)}
            </Typography>
          </div>

          {milestone.description && (
            <div>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {intl.formatMessage({ id: 'milestone.field.description' })}
              </Typography>
              <MarkdownContainer className="markdown-body">
                <MarkdownPreview text={milestone.description} />
              </MarkdownContainer>
            </div>
          )}

          <MetaInfoBox>
            <MetaInfoContent>
              <Typography variant="caption" color="text.secondary">
                {intl.formatMessage({ id: 'milestone.field.created' })}:
              </Typography>
              {milestone.createdBy?.iconUrl && (
                <SmallAvatar src={milestone.createdBy.iconUrl} />
              )}
              <Typography variant="caption" color="text.secondary">
                {milestone.createdBy?.name} ({milestone.createdAt})
              </Typography>
            </MetaInfoContent>
            {milestone.updatedAt && (
              <MetaInfoContent>
                <Typography variant="caption" color="text.secondary">
                  {intl.formatMessage({ id: 'milestone.field.updated' })}:
                </Typography>
                {milestone.updatedBy?.iconUrl && (
                  <SmallAvatar src={milestone.updatedBy.iconUrl} />
                )}
                <Typography variant="caption" color="text.secondary">
                  {milestone.updatedBy?.name} ({milestone.updatedAt})
                </Typography>
              </MetaInfoContent>
            )}
          </MetaInfoBox>
        </FieldsContainer>
      </PanelContent>
    </>
  )
}

const FormPanel = ({
  formState,
  onFieldChange,
}: {
  formState: FormState
  onFieldChange: (field: keyof FormState, value: string | boolean) => void
}) => (
  <>
    <PanelHeader>
      <Typography variant="subtitle2" fontWeight={600}>
        {intl.formatMessage({ id: 'milestone.panel.edit' })}
      </Typography>
    </PanelHeader>

    <PanelContent>
      <FieldsContainer>
        <MilestoneFormFields
          formState={formState}
          onFieldChange={onFieldChange}
          showAchievedToggle={true}
        />
      </FieldsContainer>
    </PanelContent>
  </>
)

// Dialog
const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    width: 1000,
    height: 770,
  },
})

const StyledDialogTitle = styled(DialogTitle)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  paddingBottom: 16,
})

const TitleContent = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
})

const StyledFlagIcon = styled(FlagIcon)({
  color: colorPalette.skyBlue[7],
})

const UnsavedChip = styled(Chip)({
  marginLeft: 8,
  backgroundColor: colorPalette.green[0],
  color: colorPalette.green[7],
  border: `1px solid ${colorPalette.green[6]}`,
})

const StyledDialogContent = styled(DialogContent)({
  padding: 0,
  flex: 1,
  overflow: 'hidden',
})

const ContentWrapper = styled('div')({
  display: 'flex',
  height: '100%',
})

const RightPanelWrapper = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
})

const EmptyStateContainer = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: colorPalette.monotone[4],
  gap: 8,
})

const RightPanelFooter = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  padding: 16,
  borderTop: `1px solid ${colorPalette.monotone[2]}`,
})

const SaveButton = styled(Button)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
})

// LeftPanel
const LeftPanelContainer = styled('div')({
  width: 380,
  borderRight: `1px solid ${colorPalette.monotone[2]}`,
  display: 'flex',
  flexDirection: 'column',
})

const LeftPanelHeader = styled('div')({
  padding: 16,
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
})

const SortContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
})

const StyledSortIcon = styled(SortIcon)({
  fontSize: 16,
  color: colorPalette.monotone[4],
})

const StyledFormControl = styled(FormControl)({
  minWidth: 180,
})

const StyledSelect = styled(Select)({
  fontSize: 12,
  '& .MuiSelect-select': {
    paddingTop: 6,
    paddingBottom: 6,
  },
})

const StyledList = styled(List)({
  flex: 1,
  overflow: 'auto',
  paddingTop: 0,
  paddingBottom: 0,
})

const LoadingContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: 48,
  paddingBottom: 48,
})

const EmptyListContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: 32,
  paddingBottom: 32,
})

// MilestoneListItem
const StyledListItem = styled(ListItem)<{
  isSelected: boolean
}>(({ isSelected }) => ({
  position: 'relative',
  borderBottom: `1px solid ${colorPalette.monotone[1]}`,
  cursor: 'pointer',
  backgroundColor: isSelected ? colorPalette.skyBlue[1] : 'transparent',
  '&:hover': {
    backgroundColor: isSelected
      ? colorPalette.skyBlue[1]
      : colorPalette.monotone[1],
  },
}))

const ActionButtonsContainer = styled('div')({
  display: 'flex',
  gap: 8,
})

const AchievedIconButton = styled(IconButton)({
  marginRight: 4,
  padding: 4,
})

const StyledCheckCircleIcon = styled(CheckCircleIcon)({
  fontSize: 20,
  color: colorPalette.green[5],
})

const StyledCheckCircleOutlineIcon = styled(CheckCircleOutlineIcon)({
  fontSize: 20,
  color: colorPalette.monotone[4],
})

const StyledListItemIcon = styled(ListItemIcon)<{ iconColor: string }>(
  ({ iconColor }) => ({
    minWidth: 28,
    color: iconColor,
  })
)

// ViewPanel / FormPanel
const PanelHeader = styled('div')({
  padding: 16,
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
})

const PanelHeaderContent = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const PanelContent = styled('div')({
  padding: 16,
  flex: 1,
  overflow: 'auto',
})

const FieldsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
})

const StatusBadge = styled('div')<{
  status: 'achieved' | 'overdue' | 'pending'
}>(({ status }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: 12,
  borderRadius: 4,
  minWidth: 200,
  backgroundColor:
    status === 'achieved'
      ? colorPalette.green[0]
      : status === 'overdue'
        ? colorPalette.pink[0]
        : colorPalette.monotone[1],
}))

const StatusText = styled(Typography)<{
  status: 'achieved' | 'overdue' | 'pending'
}>(({ status }) => ({
  color:
    status === 'achieved'
      ? colorPalette.green[7]
      : status === 'overdue'
        ? colorPalette.pink[7]
        : colorPalette.monotone[6],
}))

const StyledCheckCircleIconView = styled(CheckCircleIcon)({
  color: colorPalette.green[5],
})

const StyledCheckCircleOutlineIconView = styled(CheckCircleOutlineIcon)({
  color: colorPalette.monotone[4],
})

const MetaInfoBox = styled('div')({
  backgroundColor: colorPalette.monotone[1],
  padding: 12,
  borderRadius: 4,
  marginTop: 8,
})

const MetaInfoContent = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
})

const SmallAvatar = styled(Avatar)({
  width: 16,
  height: 16,
})

const SmallEditIcon = styled(EditIcon)({
  fontSize: 16,
})

const SmallDeleteIcon = styled(DeleteIcon)({
  fontSize: 16,
})

const MarkdownContainer = styled('div')({
  fontSize: 14,
})

const DiscardConfirmDialog = styled(Dialog)`
  & .MuiDialog-paper {
    min-width: 340px;
  }
`

export default MilestoneDialog
